const translations = {
  en: {
    nav: {
      home: 'Home',
      gallery: 'Gallery',
      about: 'About',
      lessons: 'Lessons',
      contact: 'Contact',
      lessonItems: [
        { label: 'The Nine Circles', href: '#services' },
        { label: 'The Divine Clinic', href: '#lesson-clinic' },
        { label: 'Hellhound Training', href: '#lesson-hellhound' },
        { label: 'The Interrogation', href: '#lesson-interrogation' },
        { label: 'The Executioner', href: '#lesson-gallows' },
        { label: 'The Inquisition', href: '#lesson-inquisition' },
        { label: 'The Slaughterhouse', href: '#lesson-swine' },
      ],
    },
    hero: {
      role: 'Dominant',
      subtitle: 'Abandon all hope, ye who enter here',
      tagline: 'A journey through the circles of submission',
      cta: 'Descend',
      inscription: 'Per me si va nella città dolente.',
    },
    about: {
      title: 'Who Is Master Dante',
      body: `Like the poet who walked through the nine circles, Master Dante guides his subjects through realms they have never dared to explore. With precision, command, and absolute authority, each session is a descent into surrender — a transformation wrought in fire and shadow. What waits beyond the threshold is not punishment, but revelation.`,
      quote: '"In the middle of the journey of our life, I came to myself in a dark wood."',
      quoteAuthor: '— Dante Alighieri, Inferno',
      hedoneLabel: 'Operating from Hedone, Prague',
      hedoneUrl: 'https://hedone.cz',
    },
    gallery: {
      title: 'The Gallery',
      subtitle: 'Witness the descent',
    },
    services: {
      title: 'The Nine Circles',
      subtitle: 'Each circle is a step deeper. Descend in order.',
      bookCta: 'Reserve Your Session',
      items: [
        {
          numeral: 'I',
          name: 'Limbo',
          label: 'Anticipation',
          dante: 'The edge of the abyss — neither punished nor free.',
          practical: 'Anticipation is everything. You may lose your sight, or merely your sense of time — it is uncertain how long you will linger here. Light sensory deprivation and a great deal of teasing await. The only certainty in Limbo is uncertainty.',
        },
        {
          numeral: 'II',
          name: 'Lust',
          label: 'The Tease',
          dante: 'Souls swept endlessly by their own desires.',
          practical: 'The journey has only just begun. It is far too early to reach any sort of climax, sinner — is it not?',
        },
        {
          numeral: 'III',
          name: 'Gluttony',
          label: 'Consumption',
          dante: 'Excess laid bare in the cold rain.',
          practical: 'Something will be consumed — or perhaps imbibed. What exactly is left entirely to my judgment... and your own dark desires.',
        },
        {
          numeral: 'IV',
          name: 'Greed',
          label: 'The Toll',
          dante: 'The weight of want — hoarded, never satisfied.',
          practical: 'Wastefulness is also a sin of this circle. I hope you passed through the third ring without incident, sinner — otherwise, we shall be lingering here a while.',
        },
        {
          numeral: 'V',
          name: 'Wrath',
          label: 'The Claws',
          dante: 'Rage that consumes even in the dark waters.',
          practical: 'Sinners are torn asunder. Fortunately for me, I have claws.',
        },
        {
          numeral: 'VI',
          name: 'Heresy',
          label: 'The Inquisition',
          dante: 'Those who denied the order of things.',
          practical: 'An inquisition is in order, sinner.',
        },
        {
          numeral: 'VII',
          name: 'Violence',
          label: 'The Arsenal',
          dante: 'Three rings — against others, self, and God.',
          practical: 'I shall leave this to your imagination. Suffice to say that my arsenal of instruments for creating pain is considerable.',
        },
        {
          numeral: 'VIII',
          name: 'Fraud',
          label: 'The Reckoning',
          dante: 'Deceivers of those who placed no special trust in them.',
          practical: 'Time to pay for your uncovered sins, sinner. The punishment shall involve whips.',
        },
        {
          numeral: 'IX',
          name: 'Treachery',
          label: 'The Frozen Lake',
          dante: 'The frozen lake. The deepest betrayal.',
          practical: 'At the center of hell lies a frozen lake. Can you endure the cold?',
        },
      ],
    },
    rules: {
      title: 'Rules of the Session',
      subtitle: 'These are not suggestions.',
      items: [
        {
          title: 'The Covenant',
          body: 'Punctuality is mandatory. Arrive sober and clear-headed — intoxicated or high clients are turned away without refund. Photography and recording are strictly forbidden unless explicitly agreed in writing before the session. Disrespect outside of agreed roleplay ends the session immediately. My time is my own; when you are given it, treat it accordingly.',
        },
        {
          title: 'The Unbreachable',
          body: 'There are things I do not do: scat, rim jobs, sex with men, and choking to the point of unconsciousness. Safe words are always active and always respected. Read this carefully. None of it is open for negotiation.',
        },
        {
          title: 'The All-Seeing Eye',
          body: 'Misrepresenting your experience, health, or intentions in the questionnaire will end the session the moment I discover it. Attempts to push beyond my limits, chargebacks, or providing false contact details result in permanent blacklisting. I extend complete discretion to every client. I expect the same in return. Deception here is not forgotten.',
        },
        {
          title: 'The Eternal Sentence',
          body: 'No-shows without 24 hours notice forfeit their deposit permanently. Two cancellations within six months result in full prepayment being required for all future bookings.',
        },
        {
          title: 'The Toll',
          body: 'Sessions are available in 60, 90, and 120-minute durations. A deposit may be required to confirm a booking — at my discretion, though regular clients who reliably keep their appointments will generally not be asked for one. Rates vary by session length and any applicable add-ons, which I am happy to detail on request. Those who open negotiations by asking for discounts are not the clients I seek.',
        },
      ],
    },
    lessons: {
      title: 'Other Sessions',
      subtitle: 'Beyond the nine circles, other torments await.',
      items: [
        {
          id: 'clinic',
          title: 'The Divine Clinic',
          subtitle: 'Hell is an unsanitary realm. You should be examined.',
          dante: '"So does the body fall in which all sin has root."',
          body: 'Let Master — Doctor — Dante take a proper look at you. There are a whole number of tests that need to be run. I spotted some tentacles in the lust ring, so we should make sure your orifices are nice and ready for when you get to say hello to them, in the future.',
          theme: 'clinic',
        },
        {
          id: 'hellhound',
          title: 'Hellhound Training',
          subtitle: 'Even the beasts of hell must be broken.',
          dante: '"Cerberus, cruel and strange, with three throats barks dog-like over the people who are submerged here."',
          body: 'Would my newest mutt like a treat? Then beg for it. I will train you to be a fine beast of hell — obedient, devoted, utterly broken in — if you survive the training.',
          theme: 'beast',
        },
        {
          id: 'interrogation',
          title: 'The Interrogation',
          subtitle: 'Every sinner talks. Eventually.',
          dante: '"There is no sin so well hidden that the fire will not draw it into the light."',
          body: 'A crime has been committed — theft, murder, or something darker still — and you are the one who knows. Bound beneath the bare light, you will answer for it: names, places, the hour of the deed. Confess it freely and I may be gentle; make me work for it and I shall. Either way, sinner, before this session ends I will have the truth — and you will have learned the price of keeping secrets from me.',
          theme: 'scenario',
          glyph: 'interrogation',
        },
        {
          id: 'gallows',
          title: 'The Executioner',
          subtitle: 'The crowd is waiting. So is the rope.',
          dante: '"Here sighs and lamentations and loud cries resounded through the starless air."',
          body: 'Your sentence is passed, the scaffold built, and the crowd has gathered to watch you die. As your executioner it falls to me to prepare you for the end — the noose, or whatever method I judge fitting. But first comes the punishment you have earned: the humiliation, the parading, the slow breaking of what remains of your pride. A good execution is theatre, sinner. Let us give them a show worth remembering.',
          theme: 'scenario',
          glyph: 'gallows',
        },
        {
          id: 'inquisition',
          title: 'The Inquisition',
          subtitle: 'Confess — or be made to.',
          dante: '"Confess thy sins, that the fire may cleanse what the tongue will not."',
          body: 'You come before the tribunal a sinner and a heretic, burdened with every transgression of flesh and spirit. Kneel, and confess them to me one by one. For each sin you name, a penance; for each you try to conceal, something far less merciful. Whether your confession flows freely, or whether it must be drawn from you by other means, is entirely your choice... and mine. The Inquisitor is patient. The Inquisitor is thorough.',
          theme: 'scenario',
          glyph: 'inquisition',
        },
        {
          id: 'swine',
          title: 'The Slaughterhouse',
          subtitle: 'Not yet fit for the table.',
          dante: '"Excess laid bare — the swine that wallow in their own filth."',
          body: 'You are no man here; you are livestock. A filthy pig, unwashed and unfit for consumption. Before you can be made edible you must be processed — inspected, cleaned, graded, and prepared to my exacting standards. Squeal all you wish, swine; it changes nothing. By the time I am done, you will know precisely what you are, and precisely what you are worth: meat.',
          theme: 'scenario',
          glyph: 'swine',
        },
      ],
    },
    contact: {
      title: 'Seek Audience',
      subtitle: 'The gates open only to those who ask',
      name: 'Your Name',
      email: 'Your Email',
      message: 'Your Message',
      send: 'Send',
      note: 'All inquiries are held in strict confidence.',
      bookTitle: 'Reserve a Date',
      bookBody: 'Sessions can be reserved directly through Hedone — Prague\'s professional BDSM studio — using their booking calendar. You can also reach me through the enquiry form below. Either way, a booking is confirmed only after I have reviewed your submission and a deposit is received.',
      bookCta: 'Book via Hedone',
      bookUrl: 'https://bdsmlekce.cz/kalendar/',
    },
    footer: {
      rights: 'All rights reserved.',
      tagline: 'Per me si va nella città dolente.',
    },
  },
  cs: {
    nav: {
      home: 'Domů',
      gallery: 'Galerie',
      about: 'O mně',
      lessons: 'Lekce',
      contact: 'Kontakt',
      lessonItems: [
        { label: 'Devět Kruhů', href: '#services' },
        { label: 'Božská Klinika', href: '#lesson-clinic' },
        { label: 'Výcvik Pekelného Ohaře', href: '#lesson-hellhound' },
        { label: 'Výslech', href: '#lesson-interrogation' },
        { label: 'Kat', href: '#lesson-gallows' },
        { label: 'Inkvizice', href: '#lesson-inquisition' },
        { label: 'Jatka', href: '#lesson-swine' },
      ],
    },
    hero: {
      role: 'Dominant',
      subtitle: 'Zanechte veškerou naději, vy kdo vstupujete',
      tagline: 'Cesta skrze kruhy odevzdání',
      cta: 'Sestoupit',
      inscription: 'Per me si va nella città dolente.',
    },
    about: {
      title: 'Kdo Je Master Dante',
      body: `Jako básník, který prošel devíti kruhy, provází Master Dante své poddané říšemi, do nichž se nikdy neodvážili vstoupit. S precizností, autoritou a absolutní kontrolou je každé sezení sestupem do odevzdání — proměna zrozená v ohni a stínu. Za prahem nečeká trest, ale zjevení.`,
      quote: '„V polovině cesty našeho života jsem se ocitl v temném lese."',
      quoteAuthor: '— Dante Alighieri, Peklo',
      hedoneLabel: 'Lekce jsou v klubu Hedone, Praha',
      hedoneUrl: 'https://hedone.cz',
    },
    gallery: {
      title: 'Galerie',
      subtitle: 'Svědkové sestupu',
    },
    services: {
      title: 'Devět Kruhů',
      subtitle: 'Každý kruh je krok hlouběji. Čtěte v pořadí.',
      bookCta: 'Rezervovat sezení',
      items: [
        {
          numeral: 'I',
          name: 'Limbo',
          label: 'Očekávání',
          dante: 'Okraj propasti — bez trestu, ale i bez svobody.',
          practical: 'Očekávání je vše. Možná přijdeš o zrak, možná jen o čas — není jisté, jak dlouho tu budeš. Čeká tě lehká smyslová deprivace a mnoho drážděni. Jedinou jistotou v Limbu je nejistota.',
        },
        {
          numeral: 'II',
          name: 'Chtíč',
          label: 'Dráždění',
          dante: 'Duše věčně unášené vlastními touhami.',
          practical: 'Naše cesta teprve začíná. Na vyvrcholení je zatím příliš brzy, hříšníku — nemyslíš?',
        },
        {
          numeral: 'III',
          name: 'Obžerství',
          label: 'Konzumace',
          dante: 'Přebytky odhalené v chladném dešti.',
          practical: 'Něco bude konzumováno — nebo snad vypito. Co přesně záleží výhradně na mém posouzení... a tvých temných tužbách.',
        },
        {
          numeral: 'IV',
          name: 'Lakota',
          label: 'Mýtné',
          dante: 'Tíha touhy — hromaděné, nikdy nenaplněné.',
          practical: 'Marnotratnost je také hříchem tohoto kruhu. Doufám, že jsi prošel třetím kruhem bez problémů, hříšníku — jinak se tu na chvíli zdržíme.',
        },
        {
          numeral: 'V',
          name: 'Hněv',
          label: 'Drápy',
          dante: 'Zlost pohlcující i v temných vodách.',
          practical: 'Hříšníci budou roztrháni. Naštěstí pro mě mám drápy.',
        },
        {
          numeral: 'VI',
          name: 'Kacířství',
          label: 'Inkvizice',
          dante: 'Ti, kdo popírali řád věcí.',
          practical: 'Je čas na Inkvizici, hříšníku.',
        },
        {
          numeral: 'VII',
          name: 'Násilí',
          label: 'Arsenal',
          dante: 'Tři okruhy — vůči druhým, sobě a Bohu.',
          practical: 'Nechám tuto část tvé představivosti. Stačí říci, že mých nástrojů k vytváření bolesti je více než dost.',
        },
        {
          numeral: 'VIII',
          name: 'Podvod',
          label: 'Vyúčtování',
          dante: 'Podvodníci těch, kteří v ně vložili důvěru.',
          practical: 'Čas zaplatit za odhalené hříchy, hříšníku. Trest bude nějak souviset s důtkami.',
        },
        {
          numeral: 'IX',
          name: 'Zrada',
          label: 'Zamrzlé jezero',
          dante: 'Zamrzlé jezero. Nejhlubší zrada.',
          practical: 'V nitru pekelném je zamrzlé jezero. Dokážeš odolat chladu?',
        },
      ],
    },
    rules: {
      title: 'Pravidla Lekcí',
      subtitle: 'Toto nejsou pouhá doporučení.',
      items: [
        {
          title: 'Smlouva',
          body: 'Dochvilnost je povinná. Přijďte střízliví — opilí, či zfetovaní klienti jsou odmítnuti bez vrácení zálohy. Fotografování a nahrávání je přísně zakázáno, pokud není výslovně písemně sjednáno před sezením. Neúcta mimo dohodnutý roleplay okamžitě ukončuje sezení. Můj čas je můj; pokud vám ho věnuji, nakládejte s ním odpovídajícím způsobem.',
        },
        {
          title: 'Nepřekročitelné',
          body: 'Existují věci, které nedělám: scat, rim joby, sex s mužy a dušení na pokraj bezvědomí. Bezpečnostní slova jsou vždy aktivní a vždy respektována. Přečtěte si toto pozorně. Nic z toho není předmětem vyjednávání.',
        },
        {
          title: 'Vševidoucí Oko',
          body: 'Zkreslení vaší zkušenosti, zdravotního stavu nebo záměrů v dotazníku ukončí sezení ve chvíli, kdy na to přijdu. Pokusy o překročení mých hranic, storna plateb nebo poskytnutí nepravdivých kontaktních údajů vedou k trvalému blacklistu. Každému klientovi poskytnu naprostou diskrétnost. Očekávám totéž na oplátku. Podvod zde není zapomenut.',
        },
        {
          title: 'Věčný Rozsudek',
          body: 'Nedostavení se bez 24hodinového upozornění znamená trvalé propadnutí zálohy. Dvě zrušení během šesti měsíců vedou k požadavku plné platby předem u všech budoucích rezervací.',
        },
        {
          title: 'Výpalné',
          body: 'Sezení jsou dostupná v délce 60, 90 a 120 minut. Záloha může být vyžadována dle mého uvážení — spolehliví stálí klienti ji zpravidla neplatí. Sazby se liší podle délky a případných příplatků, které rád upřesním na požádání. Ti, kteří zahájí jednání žádostí o slevu, nejsou klienti, které hledám.',
        },
      ],
    },
    lessons: {
      title: 'Ostatní Lekce',
      subtitle: 'Za devíti kruhy čekají další trýzně.',
      items: [
        {
          id: 'clinic',
          title: 'Božská Klinika',
          subtitle: 'Peklo je říší špinavou. Měl by sis nechat udělat prohlídku.',
          dante: '"Tak padá tělo, v němž má každý hřích svůj kořen."',
          body: 'Nech se prohlédnout u Mastera — Doktora — Danteho. Musíme na tobě udělat celou řadu testů. V kruhu chtíče jsem zahlédl chapadla — měli bychom se ujistit, že jsou tvé otvory pro ně hezky připravené, až je půjdeš pozdravit.',
          theme: 'clinic',
        },
        {
          id: 'hellhound',
          title: 'Výcvik Pekelného Ohaře',
          subtitle: 'I bestie pekelné musí být zkroceny.',
          dante: '"Kerberos, ukrutná příšera, štěká třemi hrdly nad lidmi zde ponořenými."',
          body: 'Chce můj nový čokl pamlsek? Tak žebrej. Vycvičím z tebe úctyhodného ohaře pekelného — poslušného, oddaného, zcela zlomeného — pokud zvládneš projít přísným výcvikem.',
          theme: 'beast',
        },
        {
          id: 'interrogation',
          title: 'Výslech',
          subtitle: 'Každý hříšník nakonec promluví.',
          dante: '"Není hříchu tak dobře ukrytého, aby jej oheň nevytáhl na světlo."',
          body: 'Došlo ke zločinu — ke krádeži, vraždě či něčemu horšímu — jen ty znáš pravdu. Teď sedíš spoutaný v ostrém světle a budeš odpovídat. Chci jména, místa i přesný čas činu. Přiznej se bez odporu a možná s tebou budu zacházet shovívavě.  Zapírej a poznej můj hněv. Ať už se rozhodneš jakkoli, než toto sezení skončí, budu znát pravdu. A ty pochopíš, jak draze se platí za tajemství ukrytá přede mnou.',
          theme: 'scenario',
          glyph: 'interrogation',
        },
        {
          id: 'gallows',
          title: 'Kat',
          subtitle: 'Dav čeká. A provaz také.',
          dante: '"Zde vzdechy, nářky a hlasitý pláč zněly bezhvězdným vzduchem."',
          body: 'Rozsudek byl vynesen, popraviště je připraveno a dav už netrpělivě čeká na tvůj konec. Mým úkolem je připravit tě na smrt — oprátkou, nebo jiným způsobem, který pro tebe uznám za vhodný. Ještě předtím však přijde trest, který sis vysloužil: veřejné ponížení, potupná přehlídka a pomalé lámání posledních zbytků tvé hrdosti. Poprava není pouhé usmrcení, hříšníku. Je to představení. A my lidem dopřejeme podívanou, na kterou jen tak nezapomenou.',
          theme: 'scenario',
          glyph: 'gallows',
        },
        {
          id: 'inquisition',
          title: 'Inkvizice',
          subtitle: 'Zpovídej se — nebo budeš přinucen.',
          dante: '"Vyznej své hříchy, ať oheň očistí to, co jazyk nechce."',
          body: 'Předstupuješ před tribunál jako hříšník a kacíř, obtížený proviněními těla i duše. Poklekni a vyznej mi každý svůj hřích, jeden po druhém. Za to, co přiznáš, tě čeká pokání. Za to, co se pokusíš zatajit, trest mnohem méně milosrdný. Záleží jen na tobě, zda ze sebe svou zpověď vydáš dobrovolně, či zda ji z tebe budu muset dostat jinými prostředky. Tedy alespoň zčásti na tobě. Inkvizitor má dosti času. A nepřestane, dokud neuslyší úplně všechno, čeho se tvá prohnilá duše dopustila.',
          theme: 'scenario',
          glyph: 'inquisition',
        },
        {
          id: 'swine',
          title: 'Jatka',
          subtitle: 'Ještě ne na talíř.',
          dante: '"Obnažená nestřídmost — svině válející se ve vlastní špíně."',
          body: 'Tady nejsi člověkem. Jsi dobytek — špinavé, hanebné, nechutné prase, které zatím není hodno porážky ani stolu. Než se z tebe stane použitelný kus masa, musíš projít důkladnou kontrolou, očistou a zpracováním podle mých přesných požadavků. Budu tě prohlížet, hodnotit a připravovat tak dlouho, dokud nebudeš odpovídat mým nárokům. Kvič si, jak chceš, svině. Na tom, co s tebou bude, to nic nezmění. Až skončím, nebudeš mít žádné pochybnosti o tom, co jsi ani jakou máš cenu: jsi jen maso.',
          theme: 'scenario',
          glyph: 'swine',
        },
      ],
    },
    contact: {
      title: 'Žádost o Audienci',
      subtitle: 'Brány se otevírají jen těm, kdo se zeptají',
      name: 'Vaše jméno',
      email: 'Váš e-mail',
      message: 'Vaše zpráva',
      send: 'Odeslat',
      note: 'Veškerá korespondence je přísně důvěrná.',
      bookTitle: 'Rezervovat termín',
      bookBody: 'Sezení lze rezervovat přímo přes Hedone — pražské profesionální BDSM studio — prostřednictvím jejich rezervačního kalendáře. Můžete mě také kontaktovat přes formulář níže. V každém případě je rezervace potvrzena až po přezkoumání vaší žádosti a přijetí zálohy.',
      bookCta: 'Rezervovat přes Hedone',
      bookUrl: 'https://bdsmlekce.cz/kalendar/',
    },
    footer: {
      rights: 'Všechna práva vyhrazena.',
      tagline: 'Per me si va nella città dolente.',
    },
  },
};

export default translations;
